// src/pages/admin/ExtWarrantyProductsList.tsx (TABLE VIEW)
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Delete,
  Refresh,
  NavigateNext,
  Close,
  Shield,
  Search,
  Visibility,
  FilterList,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListAllWarrantiesQuery,
  useListWarrantiesByProductQuery,
  useDeleteWarrantyMutation,
  Warranty,
  WARRANTY_PERIOD_OPTIONS,
} from '../../features/admin/warrantyApi.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
};

const ExtWarrantyProductsList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchStr, setSearchStr] = useState('');
  
  // Filter states
  const [viewMode, setViewMode] = useState<'all' | 'product'>('all');
  const [selectedProductFilter, setSelectedProductFilter] = useState<number | null>(null);

  // View details dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState<Warranty | null>(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // API hooks - list all warranties
  const {
    data: allWarrantiesData,
    isLoading: isLoadingAll,
    error: errorAll,
    isError: isErrorAll,
    refetch: refetchAll
  } = useListAllWarrantiesQuery(undefined, {
    skip: viewMode !== 'all'
  });

  // API hooks - List warranties by product
  const {
    data: productWarrantiesData,
    isLoading: isLoadingProduct,
    error: errorProduct,
    isError: isErrorProduct,
    refetch: refetchProduct
  } = useListWarrantiesByProductQuery(
    { product_id: selectedProductFilter || 0 },
    { skip: viewMode !== 'product' || !selectedProductFilter }
  );

  // Products list for dropdown
  const { data: productsResponse } = useListProductsQuery({
    page: 1,
    limit: 1000,
    search_str: '',
  });

  const [deleteWarranty, { isLoading: isDeleting }] = useDeleteWarrantyMutation();

  // Handle API response structure
  const warranties = viewMode === 'all' 
    ? (allWarrantiesData?.data || [])
    : (productWarrantiesData?.data || []);
  
  const totalRecords = viewMode === 'all'
    ? (allWarrantiesData?.count || 0)
    : (productWarrantiesData?.count || 0);

  const isLoading = viewMode === 'all' ? isLoadingAll : isLoadingProduct;
  const isError = viewMode === 'all' ? isErrorAll : isErrorProduct;
  const error = viewMode === 'all' ? errorAll : errorProduct;

  const products = productsResponse?.result || productsResponse?.data || [];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (warranty: Warranty) => {
    setSelectedWarranty(warranty);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedWarranty(null);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this warranty?')) {
      try {
        const result = await deleteWarranty({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warranty deleted successfully!',
          severity: 'success'
        });
        if (viewMode === 'all') {
          refetchAll();
        } else {
          refetchProduct();
        }
      } catch (err: any) {
        console.error('Failed to delete warranty:', err);

        let errorMessage = 'Error deleting warranty. Please try again.';

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleRefresh = () => {
    if (viewMode === 'all') {
      refetchAll();
    } else {
      refetchProduct();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view warranties.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No warranties found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load warranties. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatWarrantyPeriod = (period: string, count: number) => {
    return `${count} ${period}${count > 1 ? 's' : ''}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const getProductName = (productId: number) => {
    const product = products.find((p: Product) => (p.product_id || p.id) === productId);
    return product?.prod_name || 'Unknown Product';
  };

  // Filter warranties based on search
  const filteredWarranties = warranties.filter((warranty) => {
    if (!searchStr) return true;
    const searchLower = searchStr.toLowerCase();
    return (
      warranty.warranty_name?.toLowerCase().includes(searchLower) ||
      warranty.prod_name?.toLowerCase().includes(searchLower) ||
      warranty.description?.toLowerCase().includes(searchLower)
    );
  });

  // Paginate warranties
  const paginatedWarranties = filteredWarranties.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link underline="hover" color="inherit" href="/adminDashboard">Dashboard</Link>
              <Typography color="text.primary">Ext. Warranty Products List</Typography>
            </Breadcrumbs>
            
            <Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: shopifyColors.textPrimary,
                mb: 1
              }}>
                Extended Warranty Products List
              </Typography>
              <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                View and manage warranties assigned to products
              </Typography>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* View Mode Toggle */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>View Mode</InputLabel>
                    <Select
                      value={viewMode}
                      onChange={(e) => {
                        setViewMode(e.target.value as 'all' | 'product');
                        setPage(0);
                      }}
                      label="View Mode"
                      sx={{ borderRadius: '6px' }}
                    >
                      <MenuItem value="all">All Warranties</MenuItem>
                      <MenuItem value="product">By Product</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Product Filter - Only show in product mode */}
                  {viewMode === 'product' && (
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel>Select Product</InputLabel>
                      <Select
                        value={selectedProductFilter || ''}
                        onChange={(e) => {
                          setSelectedProductFilter(Number(e.target.value));
                          setPage(0);
                        }}
                        label="Select Product"
                        sx={{ borderRadius: '6px' }}
                      >
                        <MenuItem value="">
                          <em>Choose a product</em>
                        </MenuItem>
                        {products.map((product: Product) => (
                          <MenuItem key={product.product_id || product.id} value={product.product_id || product.id}>
                            {product.prod_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search warranties by name, product or description..."
                    value={searchStr}
                    onChange={(e) => {
                      setSearchStr(e.target.value);
                      setPage(0);
                    }}
                    sx={{
                      minWidth: 300,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                Total {filteredWarranties.length} extended warrant{filteredWarranties.length === 1 ? 'y' : 'ies'} found
                {searchStr && ` matching "${searchStr}"`}
                {viewMode === 'product' && selectedProductFilter && ' for selected product'}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading warranties...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredWarranties.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <Shield sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No warranties found' : 'No warranties available'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No warranties match your search "${searchStr}"`
                    : viewMode === 'product' && !selectedProductFilter
                    ? 'Select a product to view its warranties'
                    : 'No extended warranties have been added yet'
                  }
                </Typography>
              </Card>
            )}

            {/* Warranties Table */}
            {!isLoading && filteredWarranties.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: shopifyColors.background }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>Warranty Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedWarranties.map((warranty) => (
                        <TableRow key={warranty.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              #{warranty.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ bgcolor: shopifyColors.blue, width: 32, height: 32 }}>
                                <Shield sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {warranty.warranty_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: shopifyColors.textPrimary }}>
                              {warranty.prod_name || getProductName(warranty.product_id)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={formatWarrantyPeriod(warranty.warranty_period, warranty.no_of_period)}
                              size="small"
                              sx={{
                                backgroundColor: shopifyColors.blue,
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={warranty.description}>
                              <Typography variant="body2" sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {warranty.description}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {warranty.price > 0 ? (
                              <Typography variant="body2" sx={{ color: shopifyColors.primary, fontWeight: 600 }}>
                                {formatCurrency(warranty.price)}
                                {warranty.additional_cost > 0 && (
                                  <Typography component="span" variant="caption" sx={{ color: shopifyColors.warning, ml: 0.5 }}>
                                    +{formatCurrency(warranty.additional_cost)}
                                  </Typography>
                                )}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                Free
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(warranty)}
                                  sx={{ color: shopifyColors.blue }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(warranty.id)}
                                  disabled={isDeleting}
                                  sx={{ color: shopifyColors.error }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Pagination */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredWarranties.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: `1px solid ${shopifyColors.border}` }}
                />
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetailDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              Warranty Details
            </Typography>
            <IconButton onClick={handleCloseDetailDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedWarranty && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: shopifyColors.blue, width: 56, height: 56 }}>
                  <Shield />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                    {selectedWarranty.warranty_name}
                  </Typography>
                  <Chip 
                    label={formatWarrantyPeriod(selectedWarranty.warranty_period, selectedWarranty.no_of_period)}
                    size="small"
                    sx={{
                      backgroundColor: shopifyColors.blue,
                      color: 'white',
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ color: shopifyColors.textSecondary, mb: 1 }}>
                  Product
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedWarranty.prod_name || getProductName(selectedWarranty.product_id)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ color: shopifyColors.textSecondary, mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body1">
                  {selectedWarranty.description}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ color: shopifyColors.textSecondary, mb: 1 }}>
                  Features
                </Typography>
                <Typography variant="body1">
                  {selectedWarranty.features}
                </Typography>
              </Box>

              {(selectedWarranty.price > 0 || selectedWarranty.additional_cost > 0) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: shopifyColors.textSecondary, mb: 1 }}>
                      Pricing
                    </Typography>
                    {selectedWarranty.price > 0 && (
                      <Typography variant="body1" sx={{ color: shopifyColors.primary, fontWeight: 600 }}>
                        Base Price: {formatCurrency(selectedWarranty.price)}
                      </Typography>
                    )}
                    {selectedWarranty.additional_cost > 0 && (
                      <Typography variant="body1" sx={{ color: shopifyColors.warning, fontWeight: 600 }}>
                        Additional Cost: {formatCurrency(selectedWarranty.additional_cost)}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, p: 2, bgcolor: '#f0f9f0', borderRadius: '6px' }}>
                      <Typography variant="body2" sx={{ color: '#00783e', fontWeight: 600 }}>
                        Total: {formatCurrency((selectedWarranty.price || 0) + (selectedWarranty.additional_cost || 0))}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseDetailDialog}
            variant="contained"
            sx={{
              backgroundColor: shopifyColors.primary,
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: shopifyColors.primaryLight,
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExtWarrantyProductsList;